import { v7 } from "uuid";
import { z } from "zod";
import { ACCOUNT_OUTBOX_KINDS, discardQuarantined, dropEntity, EMPTY_OUTBOX, enqueue, enqueueMany, isWorkoutKind, markDone, markFailed, markInFlight, OUTBOX_ITEM_STATES, OUTBOX_KINDS, recoverInFlight, release, resetBackoff, retryQuarantined, withdrawUnsent, } from "./outbox";
/**
 * The outbox, persisted.
 *
 * ── THE STORAGE IS INJECTED ────────────────────────────────────────────────
 * The app backs it with `@capacitor/preferences` (plan D21): Android may evict
 * a WebView's localStorage and IndexedDB, and this queue is the only copy of a
 * workout logged offline. On the web the same wrapper falls back to
 * localStorage. Nothing here imports Capacitor or React.
 *
 * ── ONE WRITE CHAIN ────────────────────────────────────────────────────────
 * Every mutation runs on a single promise chain: read the current state, apply
 * the pure operation, write, notify. Two enqueues in the same tick therefore
 * see each other's result instead of both starting from the same snapshot and
 * the second write erasing the first.
 *
 * ── TWO KEYS ON DISK, ONE QUEUE IN MEMORY (`D194`) ─────────────────────────
 * The workout's kinds stay under `key`, exactly as every bundle before
 * `D194` wrote and reads them. Everything else goes under `<key>.account`.
 * An older bundle — an OTA rollback — reads `key` with a schema that knows
 * five kinds and sets the WHOLE value aside as corrupt on a sixth, so a
 * profile edit stored beside a set would have hidden the set from it. Split,
 * the older bundle replays its sets and never sees the rest, which waits for
 * a bundle that knows it. In memory the two are one queue, merged by
 * `createdAt` (the workout's first on a tie), so ordering across them — a
 * feedback behind its session's end — is the same `isBlocked` rule.
 * An item under `.account` whose kind this bundle does not know (a NEWER
 * bundle wrote it) is kept as it was and written back, never dropped, and
 * never sent. A write that changes items of one key writes that key only;
 * `enqueueMany` across both kinds would be two writes, and nothing does it.
 *
 * ── A CORRUPT VALUE IS KEPT, NEVER RESET ───────────────────────────────────
 * The stored value is validated on read. One that does not parse is copied to
 * `<key>.corrupt-<timestamp>` and reported through `onCorrupt` BEFORE the
 * queue starts empty, so the next write — which overwrites `<key>` — cannot
 * destroy the only copy of what was there.
 */
export const OUTBOX_STORAGE_KEY = "mypocketgym.outbox.v1";
const outboxError = z.object({
    messageKey: z.string(),
    status: z.number().int(),
});
const outboxItem = z.object({
    // Optional, and normalised to `null` on read: every value written before the
    // field existed parses, so no migration and no bump of `version` below.
    accountId: z.string().min(1).nullable().optional(),
    attempts: z.number().int().nonnegative(),
    body: z.unknown(),
    createdAt: z.iso.datetime(),
    dependsOn: z.string().nullable(),
    entityId: z.string().min(1),
    id: z.string().min(1),
    kind: z.enum(OUTBOX_KINDS),
    lastError: outboxError.nullable(),
    nextAttemptAt: z.iso.datetime().nullable(),
    preview: z.unknown().optional(),
    state: z.enum(OUTBOX_ITEM_STATES),
});
/** One `.account` item; its kind checked separately, see the header. */
const accountItem = outboxItem.extend({ kind: z.enum(ACCOUNT_OUTBOX_KINDS) });
/** The `.account` envelope: items checked one by one, not as a whole. */
const storedAccountOutbox = z.object({
    items: z.array(z.unknown()),
    version: z.literal(1),
});
/** The on-disk envelope. `version` changes only with the key's `.v<n>`. */
const storedOutbox = z.object({
    items: z.array(outboxItem),
    version: z.literal(1),
});
function serialize(items, foreign = []) {
    return JSON.stringify({
        // Written explicitly rather than trusting the type: an item that reached
        // here without an owner is stored as `null`, never as an absent key that
        // the next reader would have to guess about.
        items: [
            ...items.map((item) => ({
                ...item,
                accountId: item.accountId ?? null,
            })),
            ...foreign,
        ],
        version: 1,
    });
}
/** The account's half of `key` (`D194`, see the header). */
export function accountOutboxKey(key) {
    return `${key}.account`;
}
/** Merge the two halves by `createdAt`, the workout's first on a tie. */
function merged(workout, account) {
    if (account.length === 0) {
        return { items: workout };
    }
    const items = [];
    let w = 0;
    let a = 0;
    while (w < workout.length || a < account.length) {
        const nextWorkout = workout[w];
        const nextAccount = account[a];
        if (nextAccount === undefined ||
            (nextWorkout !== undefined &&
                Date.parse(nextWorkout.createdAt) <= Date.parse(nextAccount.createdAt))) {
            items.push(nextWorkout);
            w += 1;
        }
        else {
            items.push(nextAccount);
            a += 1;
        }
    }
    return { items };
}
export function createOutboxStore(storage, key = OUTBOX_STORAGE_KEY, options = {}) {
    const newId = options.newId ?? (() => v7());
    const clock = options.now ?? (() => new Date());
    const listeners = new Set();
    const accountKey = accountOutboxKey(key);
    let state = EMPTY_OUTBOX;
    /** `.account` items a newer bundle wrote, kept verbatim (header). */
    let foreign = [];
    /** What each key holds on disk, serialised; `null` when absent. */
    const onDisk = {
        [accountKey]: null,
        [key]: null,
    };
    /** The one read, resolved. It carries no state: `state` is the state. */
    let loading = null;
    // The single write chain. It never rejects, so one failed write does not
    // wedge every later one; each caller still sees its own failure.
    let chain = Promise.resolve();
    const notify = () => {
        for (const listener of listeners) {
            listener(state);
        }
    };
    const preserveCorrupt = async (which, raw, reason) => {
        const savedAs = `${which}.corrupt-${clock().toISOString()}`;
        await storage.setItem(savedAs, raw);
        options.onCorrupt?.({ reason, savedAs });
    };
    const parseJson = async (which, raw) => {
        try {
            return { json: JSON.parse(raw), ok: true };
        }
        catch (cause) {
            await preserveCorrupt(which, raw, `invalid JSON: ${String(cause)}`);
            return { ok: false };
        }
    };
    const readWorkout = async () => {
        const raw = await storage.getItem(key);
        onDisk[key] = raw;
        if (raw === null) {
            return [];
        }
        const json = await parseJson(key, raw);
        if (!json.ok) {
            return [];
        }
        const parsed = storedOutbox.safeParse(json.json);
        if (!parsed.success) {
            await preserveCorrupt(key, raw, parsed.error.message);
            return [];
        }
        return parsed.data.items.map((item) => ({ ...item, accountId: item.accountId ?? null }));
    };
    const readAccount = async () => {
        const raw = await storage.getItem(accountKey);
        onDisk[accountKey] = raw;
        if (raw === null) {
            return [];
        }
        const json = await parseJson(accountKey, raw);
        if (!json.ok) {
            return [];
        }
        const parsed = storedAccountOutbox.safeParse(json.json);
        if (!parsed.success) {
            await preserveCorrupt(accountKey, raw, parsed.error.message);
            return [];
        }
        const known = [];
        const unknown = [];
        for (const candidate of parsed.data.items) {
            const item = accountItem.safeParse(candidate);
            if (item.success) {
                known.push({
                    ...item.data,
                    accountId: item.data.accountId ?? null,
                });
            }
            else {
                unknown.push(candidate);
            }
        }
        foreign = unknown;
        return known;
    };
    const read = async () => {
        const workout = await readWorkout();
        const account = await readAccount();
        return recoverInFlight(merged(workout, account));
    };
    /** Write each key whose half changed, and only those. */
    const persist = async (next) => {
        const workout = next.items.filter((item) => isWorkoutKind(item.kind));
        const account = next.items.filter((item) => !isWorkoutKind(item.kind));
        const halves = [
            [
                key,
                workout.length === 0 && onDisk[key] === null
                    ? null
                    : serialize(workout),
            ],
            [
                accountKey,
                account.length === 0 &&
                    foreign.length === 0 &&
                    onDisk[accountKey] === null
                    ? null
                    : serialize(account, foreign),
            ],
        ];
        for (const [which, value] of halves) {
            if (value !== null && value !== onDisk[which]) {
                await storage.setItem(which, value);
                onDisk[which] = value;
            }
        }
    };
    const load = () => {
        loading ??= read().then((loaded) => {
            state = loaded;
            notify();
        }, (error) => {
            // A storage that failed to answer is not an empty queue: forget the
            // attempt so the next call reads again instead of trusting nothing.
            loading = null;
            throw error;
        });
        // The read happens once; what every caller gets is the CURRENT state, not
        // the one that read returned. Otherwise a load after two enqueues resolves
        // to an empty queue while `getState()` already holds both.
        return loading.then(() => state);
    };
    /** Run `change` on the chain, persist its state, notify. */
    const mutate = (change) => {
        const run = chain.then(async () => {
            await load();
            const next = change(state);
            if (next.state !== state) {
                await persist(next.state);
                ({ state } = next);
                notify();
            }
            return next.result;
        });
        chain = run.catch(() => undefined);
        return run;
    };
    const apply = (operation) => mutate((current) => {
        const next = operation(current);
        return { result: next, state: next };
    });
    return {
        discardQuarantined: (id) => apply((current) => discardQuarantined(current, id)),
        dropEntity: (entityId) => apply((current) => dropEntity(current, entityId)),
        enqueue: (entry) => mutate((current) => {
            const next = enqueue(current, entry, { id: newId(), now: clock() });
            return { result: next.item, state: next.state };
        }),
        enqueueMany: (entries) => mutate((current) => {
            const next = enqueueMany(current, entries, {
                ids: entries.map(() => newId()),
                now: clock(),
            });
            return { result: next.items, state: next.state };
        }),
        getState: () => state,
        load,
        markDone: (id) => apply((current) => markDone(current, id)),
        markFailed: (id, failure, now, random) => apply((current) => markFailed(current, id, failure, now, random)),
        markInFlight: (id) => apply((current) => markInFlight(current, id)),
        release: (entityId) => apply((current) => release(current, entityId)),
        resetBackoff: () => apply((current) => resetBackoff(current)),
        retryQuarantined: (id) => apply((current) => retryQuarantined(current, id)),
        subscribe: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        withdrawUnsent: (entityIds) => mutate((current) => {
            const next = withdrawUnsent(current, entityIds);
            return { result: next.withdrawn, state: next.state };
        }),
    };
}
//# sourceMappingURL=store.js.map